<template>
  <div>
    <b-card>
      <TitleTable
        :titleTable="pageTitle"
        iconTable="fas fa-user-injured" />
      <b-row class="wl-filters m-1 mb-1 align-items-start">
        <b-col cols="12" md="3">
          <b-form-group label="Search" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input
              v-model="filters.search"
              placeholder="Name..."
              @keyup.enter="load(1)" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2">
          <b-form-group label="Status" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-select v-model="filters.status" :options="statusOptions">
              <template #first>
                <b-form-select-option :value="null">All</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2">
          <b-form-group label="Treatment" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.weightloss_treatment_id"
              :options="treatmentOptions"
              :clearable="true"
              placeholder="All treatments" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.facilities_id"
              :options="facilityOptions"
              :normalizer="normalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="facilityOptionsLoading" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="m-1 mb-1">
        <b-col cols="12" class="d-flex justify-content-end pb-1">
          <b-button
            variant="outline-primary"
            class="wl-filter-btn"
            :disabled="facilityOptionsLoading || loading"
            @click="load(1)">
            Filter
          </b-button>
        </b-col>
      </b-row>
      <b-row class="m-1 mb-3">
        <b-col cols="12" class="d-flex justify-content-end align-items-center pt-2 border-top">
          <GoBack class="mr-2" />
          <b-button variant="primary" @click="openEnroll">
            <i class="fas fa-user-plus mr-1"></i> Enroll patient
          </b-button>
        </b-col>
      </b-row>
      <b-table
        :items="displayItems"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No patients found">
        <template #cell(patient_type)="data">
          <b-badge :variant="patientTypeVariant(data.item)" pill>
            {{ patientTypeLabel(data.item) }}
          </b-badge>
        </template>
        <template #cell(weightloss_treatment_id)="data">
          {{ treatmentLabel(data.value, data.item) }}
        </template>
        <template #cell(facility_id)="data">
          <span :class="{ 'text-muted font-italic': !hasFacility(data.item) }">
            {{ facilityDisplay(data.item) }}
          </span>
        </template>
        <template #cell(user_id)="data">
          <span v-if="data.value" class="text-monospace">#{{ data.value }}</span>
          <span v-else class="text-muted font-italic">—</span>
        </template>
        <template #cell(status)="data">
          <span :class="{ 'text-muted font-italic': isNullish(data.value) }">
            {{ displayValue(data.value) }}
          </span>
        </template>
        <template #cell(name_insurance)="data">
          {{ insuranceName(data.item) }}
        </template>
        <template #cell(member_id)="data">
          {{ memberId(data.item) }}
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" @click="openEdit(data.item)">
            <i class="fas fa-pen"></i>
          </b-button>
        </template>
      </b-table>
      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalUsers"
        :perPage="perPage"
        :currentPage="{ page: currentPage }"
        @page-changed="load" />
    </b-card>

    <EnrollWlPatientModal
      :visible.sync="enrollModalVisible"
      :edit-item="editItem"
      :treatment-options="treatmentOptions"
      :program-context="programContext"
      @saved="onSaved"
      @error="onModalError"
      @info="onModalInfo"
      @close="editItem = null" />

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import { BCard, BRow, BCol, BTable, BButton, BFormGroup, BFormInput, BFormSelect, BBadge } from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import PaginationTable from '@/components/PaginationTable'
import ToastNotification from '@/components/ToastNotification'
import EnrollWlPatientModal from './components/EnrollWlPatientModal.vue'
import wlTreatmentsApi from '@/core/services/api/weightloss-inventory/treatments'
import wlAdminMixin from '../mixins/wlAdminMixin'
import wlLaneMixin from '../mixins/wlLaneMixin'
import axiosF from '@/core/services/api/admin/facilities'
import { userHasInsurance } from '@/core/services/api/weightloss-inventory/laneApi'

export default {
  name: 'TableWlPatients',
  mixins: [wlAdminMixin, wlLaneMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BBadge,
    BFormGroup,
    BFormInput,
    BFormSelect,
    Treeselect,
    TitleTable,
    GoBack,
    PaginationTable,
    ToastNotification,
    EnrollWlPatientModal,
  },
  data() {
    return {
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
      filters: {
        search: '',
        status: null,
        weightloss_treatment_id: null,
        facilities_id: [],
      },
      statusOptions: [
        { value: 'active', text: 'Active' },
        { value: 'paused', text: 'Paused' },
        { value: 'discharged', text: 'Discharged' },
      ],
      facilityOptions: [],
      facilityOptionsLoading: false,
      enrollModalVisible: false,
      editItem: null,
      treatmentOptions: [],
    }
  },
  computed: {
    pageTitle() {
      return this.isInsuranceLane
        ? 'GLP-1 Program Patients (Insurance)'
        : 'GLP-1 Program Patients (Regular)'
    },
    fields() {
      const base = [
        { key: 'id', label: 'WL ID' },
        { key: 'user_id', label: 'System ID' },
        { key: 'patient_type', label: 'Type' },
        { key: 'first_name', label: 'Name' },
        { key: 'last_name', label: 'Last name' },
        { key: 'weightloss_treatment_id', label: 'Treatment' },
        { key: 'facility_id', label: 'Facility' },
        { key: 'status', label: 'Status' },
      ]
      if (this.isInsuranceLane) {
        base.push(
          { key: 'name_insurance', label: 'Insurance' },
          { key: 'member_id', label: 'Member ID' }
        )
      }
      base.push({ key: 'actions', label: '' })
      return base
    },
    displayItems() {
      return this.items
    },
  },
  mounted() {
    this.init()
  },
  watch: {
    programContext() {
      this.load(1)
    },
  },
  methods: {
    async init() {
      const treatments = await wlTreatmentsApi.list()
      this.treatmentOptions = treatments.map(x => ({ id: x.id, label: x.name }))
      await this.loadFacilities()
      this.load(1)
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        this.facilityOptions = []
        this.$refs.toast?.danger(e?.message || 'Could not load facilities')
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    buildListParams() {
      return this.buildListQueryParams({
        search: this.filters.search?.trim() || null,
        status: this.filters.status,
        weightloss_treatment_id: this.filters.weightloss_treatment_id,
        facilities_id: this.filters.facilities_id,
      })
    },
    resolvePatientType(item) {
      if (this.isInsuranceLane || userHasInsurance(item?.user)) return 'insurance'
      if (item?.patient_type) return item.patient_type
      if (item?.user_id && item?.user?.patient_type) return item.user.patient_type
      return item?.user_id ? 'regular' : 'manual'
    },
    patientTypeLabel(item) {
      const type = this.resolvePatientType(item)
      if (type === 'insurance') return 'Insurance'
      if (type === 'manual') return 'Manual'
      return 'Regular'
    },
    patientTypeVariant(item) {
      const type = this.resolvePatientType(item)
      if (type === 'insurance') return 'warning'
      if (type === 'manual') return 'secondary'
      return 'primary'
    },
    insuranceName(item) {
      const n = item && item.name_insurance
      if (!n) return '—'
      return n.name || n.type || n.description || '—'
    },
    memberId(item) {
      const u = item && item.user
      return (u && u.member_id) || '—'
    },
    isNullish(value) {
      return value === null || value === undefined || value === ''
    },
    displayValue(value) {
      return this.isNullish(value) ? 'null' : value
    },
    hasFacility(item) {
      if (item?.facility?.name) return true
      const id = item?.facility_id ?? item?.facilities_id ?? item?.facility?.id
      return id != null && id !== ''
    },
    facilityDisplay(item) {
      if (item?.facility?.name) return item.facility.name
      const id = item?.facility_id ?? item?.facilities_id ?? item?.facility?.id
      if (id != null && id !== '') {
        const f = this.facilityOptions.find(x => x.id === id || x.id === Number(id))
        return f ? f.name : `#${id}`
      }
      return 'null'
    },
    treatmentLabel(id, item) {
      if (item?.treatment?.name) return item.treatment.name
      if (id == null || id === '') return 'null'
      const t = this.treatmentOptions.find(x => x.id === id || x.id === Number(id))
      return t ? t.label : `#${id}`
    },
    async load(page = 1) {
      this.loading = true
      try {
        const res = await this.wlPatientsApi.listPaginated(
          this.perPage,
          this.buildListParams(),
          page
        )
        this.applyPagination(res)
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.loading = false
      }
    },
    openEnroll() {
      this.editItem = null
      this.enrollModalVisible = true
    },
    openEdit(item) {
      this.editItem = item
      this.enrollModalVisible = true
    },
    onSaved({ mode }) {
      this.$refs.toast.success(mode === 'update' ? 'Patient updated' : 'Patient enrolled successfully')
      this.editItem = null
      this.load(this.currentPage)
    },
    onModalError(message) {
      this.$refs.toast.danger(message)
    },
    onModalInfo(message) {
      this.$refs.toast.success(message)
    },
  },
}
</script>

<style scoped>
.wl-filter-btn {
  min-width: 7rem;
}
</style>
